import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import type { Profile } from '@lr/shared';
/**
 * Stripe redirects here after a successful charge. There are two flavours:
 *
 *   - Hosted Checkout (new — used so Instagram's in-app browser works):
 *     ?session_id=cs_test_…
 *   - Stripe Elements (legacy iframe flow): ?payment_intent=pi_…&redirect_status=succeeded
 *
 * In both cases we POST to /confirm with the right id and the server
 * verifies with Stripe and credits the account. Idempotent, so it's safe
 * to call even if the webhook also fires.
 */
export function CreditsSuccess() {
  const { profile, refreshProfile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const status = params.get('redirect_status');
  const sessionId = params.get('session_id');
  const piId = params.get('payment_intent');
  const [pollingBalance, setPollingBalance] = useState<number | null>(null);

  // Confirm the purchase server-side as soon as the page lands.
  useEffect(() => {
    // Stripe Checkout doesn't include redirect_status — only the legacy
    // Elements flow does. If we have a session_id from the new hosted
    // flow, treat it as succeeded and let the server verify. Otherwise
    // require the legacy success marker.
    if (!sessionId && status !== 'succeeded') return;
    if (!sessionId && !piId) {
      // Fall back to polling /me as a last resort.
      void apiFetch<Profile>('/api/account/me')
        .then((me) => setPollingBalance(me.credit_balance))
        .catch(() => {});
      return;
    }
    let cancelled = false;
    apiFetch<{ credit_balance: number | null }>('/api/checkout/credit-purchase/confirm', {
      method: 'POST',
      body: JSON.stringify(
        sessionId ? { session_id: sessionId } : { payment_intent_id: piId }
      )
    })
      .then((r) => {
        if (cancelled) return;
        if (typeof r.credit_balance === 'number') setPollingBalance(r.credit_balance);
        if (refreshProfile) void refreshProfile();
      })
      .catch(() => {
        // Confirm failed (Stripe disabled, lookup failed, etc.) — fall back
        // to polling /me. The webhook may still credit the user shortly.
        void apiFetch<Profile>('/api/account/me')
          .then((me) => {
            if (!cancelled) setPollingBalance(me.credit_balance);
          })
          .catch(() => {});
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, sessionId, piId]);

  // Only show the "didn't go through" screen when we have NEITHER a
  // session_id (Checkout flow) NOR a succeeded redirect_status (Elements
  // flow). Either marker is enough to treat this as a success landing.
  if (!sessionId && status !== 'succeeded') {
    return (
      <section className="screen">
        <div className="scroll narrow-scroll">
          <div className="ck-head">
            <h1>PAYMENT</h1>
            <div className="p">Something didn&apos;t complete.</div>
          </div>
          <div className="empty-grid">
            <div className="h">Payment didn&apos;t go through</div>
            <div className="p">No charge was made. Try again or get in touch.</div>
            <Link
              to="/credits"
              className="chip"
              style={{ background: 'var(--ink)', color: '#fff' }}
              onClick={(e) => {
                e.preventDefault();
                navigate('/credits');
              }}
            >
              ← Back to bundles
            </Link>
          </div>
        </div>
      </section>
    );
  }
  return (
    <section className="screen">
      <div className="scroll narrow-scroll">
        <div className="success">
          <div className="check">
            <svg
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2>PAYMENT SUCCESSFUL</h2>
          <div className="p">
            Your credits have been added to your account.
          </div>
          <div className="balance-card">
            <div className="row">
              <span>Your balance</span>
              <span>
                {pollingBalance ?? profile?.credit_balance ?? 0}{' '}
                {(pollingBalance ?? profile?.credit_balance) === 1 ? 'CREDIT' : 'CREDITS'}
              </span>
            </div>
          </div>
          {/* Surface the ID-verification step here too — buying credits is
              the most engaged moment, so it's the right time to nudge any
              unverified customer to upload their ID before they get to
              checkout (where it would otherwise hold the order). */}
            {/* ID nudge hidden — remove surrounding comment to re-enable
          {profile && profile.id_status !== 'approved' && (
            <div
              style={{
                margin: '20px 16px 0',
                padding: '16px 16px',
                borderRadius: 12,
                background: 'var(--warn-bg, #f7ecd6)',
                color: 'var(--warn, #a85f00)',
                textAlign: 'left'
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: '0.16em',
                  fontWeight: 700,
                  marginBottom: 6
                }}
              >
                ONE MORE STEP
              </div>
              <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
                {profile.id_status === 'pending'
                  ? 'Your ID is in review.'
                  : profile.id_status === 'rejected'
                    ? 'Your ID upload was rejected — please re-upload.'
                    : 'Verify your ID before your first order ships.'}
              </div>
              <div style={{ fontSize: 12, marginBottom: 12 }}>
                {profile.id_status === 'pending'
                  ? 'You can keep browsing — once approved, your order will ship.'
                  : 'UK regulations mean we need to verify your ID before we can ship. It takes 30 seconds.'}
              </div>
              <Link
                to="/verify-id"
                className="cta-full"
                style={{
                  background: 'var(--ink, #0e0e0e)',
                  color: '#fff',
                  textDecoration: 'none'
                }}
              >
                <span>
                  {profile.id_status === 'pending'
                    ? 'CHECK ID STATUS'
                    : profile.id_status === 'rejected'
                      ? 'RE-UPLOAD ID'
                      : 'VERIFY MY ID'}
                </span>
                <Arrow />
              </Link>
            </div>
          )}
          */}
        </div>
        <div className="footer-cta pad-bottom">
          <Link to="/browse" className="cta-full">
            <span>BROWSE PIECES</span>
            <Arrow />
          </Link>
        </div>
      </div>
    </section>
  );
}
function Arrow() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
